import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  MenuItem, Popover, Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';

function UserEditColumn({
  member, deactivateMember, editMember, style, setupAlert, onMemberUpdate,
}) {
  const { activeWorkspace, user } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  const [userEditAnchorEl, setUserEditAnchorEl] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
  });

  const openUserEdit = Boolean(userEditAnchorEl);
  const userEditId = openUserEdit ? 'simple-popover' : undefined;

  const resendInvitation = async () => {
    const response = await sendRequest({
      method: 'post',
      url: API.WORKSPACE.resendInvitation(activeWorkspace.id, member.userId),
      data: {
        owner: user,
      },
    });
    if (response.success) {
      displayNotification({
        message: 'Resend invitation successfully.',
        severity: 'success',
      });
    } else {
      displayNotification({
        message: 'There was an issue resending invitation. Please try again',
        severity: 'error',
      });
    }
  };

  const toggleIdleTimeTracking = async () => {
    const newState = !member.idleTimeTrackingEnabled;
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.memberToggleIdleTimeTracking(activeWorkspace.id, member.id),
      data: {
        enabled: newState,
      },
    });
    if (response.success) {
      displayNotification({
        message: `Idle time tracking ${newState ? 'enabled' : 'disabled'} successfully.`,
        severity: 'success',
      });
      if (onMemberUpdate) {
        onMemberUpdate({ ...member, idleTimeTrackingEnabled: newState });
      }
    } else {
      displayNotification({
        message: 'There was an issue updating idle time tracking. Please try again',
        severity: 'error',
      });
    }
    setConfirmDialogOpen(false);
  };

  const toggleManualTracker = async () => {
    const newState = !member.manualTrackerEnabled;
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.memberToggleManualTracker(activeWorkspace.id, member.id),
      data: {
        enabled: newState,
      },
    });
    if (response.success) {
      displayNotification({
        message: `Manual tracker ${newState ? 'enabled' : 'disabled'} successfully.`,
        severity: 'success',
      });
      if (onMemberUpdate) {
        onMemberUpdate({ ...member, manualTrackerEnabled: newState });
      }
    } else {
      displayNotification({
        message: 'There was an issue updating manual tracker. Please try again',
        severity: 'error',
      });
    }
    setConfirmDialogOpen(false);
  };

  const handleToggleIdleTimeTracking = () => {
    const newState = !member.idleTimeTrackingEnabled;
    setConfirmDialogConfig({
      title: `${newState ? 'Enable' : 'Disable'} Idle Time Tracking`,
      message: `Are you sure you want to ${newState ? 'enable' : 'disable'} idle time tracking for 
      ${member.user.firstName} ${member.user.lastName}? 
      ${newState ? 'The system will track and prompt for idle periods.' : 'Idle periods will not be tracked.'}`,
      onConfirm: toggleIdleTimeTracking,
    });
    setConfirmDialogOpen(true);
    setUserEditAnchorEl(null);
  };

  const handleToggleManualTracker = () => {
    const newState = !member.manualTrackerEnabled;
    setConfirmDialogConfig({
      title: `${newState ? 'Enable' : 'Disable'} Manual Tracker`,
      message: `Are you sure you want to ${newState ? 'enable' : 'disable'} manual tracker for 
      ${member.user.firstName} ${member.user.lastName}? 
      ${newState ? 'The member will be able to manually enter time.' : 'Manual time entry will be disabled.'}`,
      onConfirm: toggleManualTracker,
    });
    setConfirmDialogOpen(true);
    setUserEditAnchorEl(null);
  };

  if (!member.status) return null;

  return (
    <div style={style}>
      <button
        type="button"
        label="edit-dropdown-button"
        onClick={(e) => setUserEditAnchorEl(e.currentTarget)}
      >
        <i className="fa fa-ellipsis-h dropdown-options" />
      </button>
      <Popover
        id={userEditId}
        open={openUserEdit}
        anchorEl={userEditAnchorEl}
        onClose={() => setUserEditAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        { !member.haveJoined
        && (
        <MenuItem
          onClick={() => { resendInvitation(member); setUserEditAnchorEl(null); }}
          data-testid="resend-invitation"
        >
          Resend Invitation
        </MenuItem>
        )}
        <MenuItem
          onClick={() => { editMember(member); setUserEditAnchorEl(null); }}
          data-testid="edit-user"
        >
          Edit User
        </MenuItem>
        <MenuItem
          onClick={() => { setupAlert(member); setUserEditAnchorEl(null); }}
          data-testid="user-alert"
        >
          Setup Alerts
        </MenuItem>
        <MenuItem
          onClick={handleToggleIdleTimeTracking}
          data-testid="toggle-idle-time-tracking"
        >
          {member.idleTimeTrackingEnabled ? '✓ Disable' : 'Enable'}
          {' '}
          Idle Time Tracking
        </MenuItem>
        <MenuItem
          onClick={handleToggleManualTracker}
          data-testid="toggle-manual-tracker"
        >
          {member.manualTrackerEnabled ? '✓ Disable' : 'Enable'}
          {' '}
          Manual Tracker
        </MenuItem>
        <MenuItem
          sx={{ color: '#B0361A' }}
          onClick={() => {
            deactivateMember(member);
            setUserEditAnchorEl(null);
          }}
          data-testid="deactivate-user"
        >
          Deactivate User
        </MenuItem>
      </Popover>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>{confirmDialogConfig.title}</DialogTitle>
        <DialogContent>
          <p style={{ marginTop: '10px' }}>{confirmDialogConfig.message}</p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDialogConfig.onConfirm}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default UserEditColumn;

UserEditColumn.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    userId: PropTypes.number.isRequired,
    status: PropTypes.bool.isRequired,
    accountType: PropTypes.string.isRequired,
    haveJoined: PropTypes.bool.isRequired,
    idleTimeTrackingEnabled: PropTypes.bool,
    manualTrackerEnabled: PropTypes.bool,
    user: PropTypes.shape({
      firstName: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      lastName: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      email: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  deactivateMember: PropTypes.func.isRequired,
  editMember: PropTypes.func.isRequired,
  style: PropTypes.shape({}),
  setupAlert: PropTypes.func.isRequired,
  onMemberUpdate: PropTypes.func,
};
